import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { EditoralImageBlockFragment } from '../types/graphqlTypes';
import { ButtonRenderer } from './buttonRenderer/ButtonRenderer';
import HtmlContent from './Html';
import { ImageCardRenderer } from './ImageCardRenderer';
import { Img } from './Img';
import { LinkList } from './LinkList';
import { Section } from './Section';
import { Spacer } from './Spacer';
import { Typography } from './Typography';

interface StyledEditorialBlockProps {
  center: boolean;
  reverse: boolean;
  isSticky: boolean;
}

export const StyledEditorialBlock = styled.div<StyledEditorialBlockProps>`
  ${({ theme: { space, media }, center, reverse, isSticky }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.lg};

    @media (min-width: ${media.lg}) {
      display: flex;
      flex-direction: ${reverse ? 'row' : 'row-reverse'};

      > * {
        flex-basis: 50%;
      }

      ${!isSticky &&
      css`
        align-items: ${center ? 'center' : 'flex-start'};
      `};

      ${isSticky &&
      css`
        .gatsby-image-wrapper {
          position: sticky;
          top: ${space.lg};
          height: min(100vh - ${space.xl}, 100%);
          max-height: 900px;
        }
      `};
    }
  `};
`;

export const StyledCopy = styled(Spacer)`
  ${({ theme: { space, media } }) => css`
    @media (min-width: ${media.lg}) {
      padding: 0 ${space.lg};
    }
  `};
`;

export const StyledButton = styled(ButtonRenderer)`
  ${({ theme: { space, media } }) => css`
    @media (min-width: ${media.lg}) {
      margin-top: ${space.xl};
    }
  `};
`;

export const EditoralImageBlock: FC<Omit<EditoralImageBlockFragment, '__typename'>> = ({
  title,
  lead,
  html,
  linkLists,
  button,
  imageRight,
  image,
  alignment,
  stickyImage,
}) => {
  const generateId = (text?: string): string => {
    if (text) {
      // Create a new variable to hold the modified text
      let modifiedText = text;

      // Remove the last full stop or question mark if it exists
      if (modifiedText.endsWith('.') || modifiedText.endsWith('?')) {
        modifiedText = modifiedText.slice(0, -1);
      }

      // Convert to lower case and replace non-alphanumeric characters with hyphens
      return modifiedText.toLowerCase().replace(/[^a-zA-Z0-9]/g, '-');
    }
    return '';
  };
  return (
    <Section contained>
      <StyledEditorialBlock
        center={alignment === 'Centre'}
        reverse={imageRight}
        isSticky={stickyImage}
        id={generateId(title)}
      >
        {/* Text column */}
        <StyledCopy>
          {/* Title */}
          {title && (
            <Typography component="h2" variant="displaySmall">
              {title}
            </Typography>
          )}

          {/* Lead */}
          {lead && (
            <Typography component="p" variant="lead">
              {lead}
            </Typography>
          )}

          {/* Html */}
          {html && <HtmlContent>{html}</HtmlContent>}

          {/* Link lists */}
          {linkLists && linkLists.length > 0 ? (
            <>
              {linkLists.map((linkList, index) => (
                <LinkList {...linkList} key={`ll-${index}`} />
              ))}
            </>
          ) : (
            ''
          )}

          {/* Button */}
          {button && <StyledButton button={button} />}
        </StyledCopy>
        {/* Image column */}
        <div>{image && <Img src={image} />}</div>
      </StyledEditorialBlock>
    </Section>
  );
};
